import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../../../components/Navbar";
import {
  Plus,
  Package,
  Search,
  ArrowUpDown,
  MoreVertical,
  Eye,
  Edit2,
  MapPin,
  MessageSquare,
  Megaphone,
  Trash2,
  CheckSquare,
  Square,
  X,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FireExtinguisher,
  Send,
  CheckCircle,
  AlertCircle,
  Lock,
  Percent,
  Clock,
  XCircle,
  Zap,
  CalendarClock,
  TimerOff,
} from "lucide-react";
import ViewOffer from "../../../components/Offers/ViewOffer";
import VendorDealTitle from "../../../components/Offers/VendorDealTitle";
import {
  getDealProductName,
  getVendorDealLabel,
} from "../../../utils/productLabels";
import { useNavigate } from "react-router-dom";
import { DealsRequest } from "../../../requests/vendor/DealsRequest";
import Loader from "../../../components/Loader";
import moment from "moment";
import { toast } from "react-toastify";
import VendorRequests from "../../../requests/vendor/VendorRequests";
import { useSelector } from "react-redux";
import PromoteDealModal from "../../../components/Offers/PromoteDealModal";
import { DemoteDealModal } from "../../../components/Offers/DemoteDealModal";
import PermissionAwareActionDropdown from "../../../components/Offers/PermissionAwareActionDropdown";
import DeleteOfferModal from "../../../components/Offers/DeleteOfferModal";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { PERMISSIONS } from "../../../config/permissions";
import LimitedAccessMessage from "../../../components/Permissions/LimitedAccessMessage";
import PermissionDeniedModal from "../../../components/Permissions/PermissionDeniedModal";

const DEAL_CREATED_TOAST_KEY = "dealboxx_deal_created_toast";

const computeDiscountPercent = (original, discounted) => {
  const orig = parseFloat(original);
  const disc = parseFloat(discounted);
  if (!orig || !disc || disc >= orig) return null;
  return Math.round(((orig - disc) / orig) * 100);
};

const getOfferPricingDetails = (offer) => {
  if (
    offer.bundle?.length > 0 &&
    offer.bundle.some((bundle) => bundle.original_sum && bundle.bundle_price)
  ) {
    const bundleItem = offer.bundle[0];
    return {
      variant: "bundle",
      original: bundleItem.original_sum,
      discounted: bundleItem.bundle_price,
      discountPercent:
        offer.discountPercentage ||
        computeDiscountPercent(
          bundleItem.original_sum,
          bundleItem.bundle_price
        ),
    };
  }

  if (
    offer.bogo?.length > 0 &&
    offer.bogo.some(
      (bogo) =>
        bogo.bogo_item_price ||
        (bogo.bogo_quantity && bogo.bogo_free_quantity)
    )
  ) {
    const bogoItem = offer.bogo[0];
    const buyQty = Number(bogoItem.bogo_quantity) || 0;
    const freeQty = Number(bogoItem.bogo_free_quantity) || 0;
    const bogoPercent =
      buyQty + freeQty > 0
        ? Math.round((freeQty / (buyQty + freeQty)) * 100)
        : null;

    return {
      variant: "bogo",
      original: bogoItem.bogo_item_price,
      discounted: null,
      discountPercent: bogoPercent,
      bogoLabel: `Buy ${buyQty} Get ${freeQty}`,
    };
  }

  if (offer.price && offer.price !== 0) {
    return {
      variant: "discount",
      original: offer.price,
      discounted: offer.discountedPrice,
      discountPercent:
        offer.discountPercentage ||
        computeDiscountPercent(offer.price, offer.discountedPrice),
    };
  }

  return {
    variant: "none",
    original: null,
    discounted: null,
    discountPercent: null,
  };
};

const DiscountPercentBadge = ({ percent }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold tracking-wide text-emerald-800 uppercase bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-100 rounded-full border border-emerald-200 shadow-sm ring-1 ring-emerald-100/80">
    <Percent className="w-3 h-3 stroke-[2.5]" />
    {Math.round(percent)}% off
  </span>
);

const STATUS_BADGE_BASE =
  "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium";

const getPlainStatusBadge = (status) => {
  switch (status) {
    case "Approved":
      return {
        label: "Approved",
        className: `${STATUS_BADGE_BASE} bg-green-100 text-green-800`,
        Icon: CheckCircle,
      };
    case "Rejected":
      return {
        label: "Rejected",
        className: `${STATUS_BADGE_BASE} bg-red-100 text-red-800`,
        Icon: XCircle,
      };
    case "Pending":
    default:
      return {
        label: status || "Pending",
        className: `${STATUS_BADGE_BASE} bg-yellow-100 text-yellow-800`,
        Icon: Clock,
      };
  }
};

const getOfferStatusBadge = (offer, isExpiredCheck) => {
  if (offer?.validity_type === "flash_sale") {
    if (offer?.flash && Array.isArray(offer.flash) && offer.flash.length > 0) {
      const isFlashExpired = offer.flash.some((flash) =>
        moment(flash.flash_end_time).isBefore(moment())
      );
      const isFlashActive = offer.flash.some((flash) =>
        moment().isBetween(
          moment(flash.flash_start_time),
          moment(flash.flash_end_time)
        )
      );

      if (isFlashActive && offer.status === "Approved") {
        return {
          label: "Flash Sale Active",
          className: `${STATUS_BADGE_BASE} bg-green-100 text-green-800 animate-pulse`,
          Icon: Zap,
        };
      }

      if (offer.status === "Pending") {
        return getPlainStatusBadge("Pending");
      }
      if (offer.status === "Rejected") {
        return getPlainStatusBadge("Rejected");
      }
      if (isFlashExpired) {
        return {
          label: "Flash Sale Expired",
          className: `${STATUS_BADGE_BASE} bg-red-100 text-red-800`,
          Icon: TimerOff,
        };
      }
      if (offer.status !== "Approved") {
        return getPlainStatusBadge(offer.status);
      }

      return {
        label: "Flash Sale Scheduled",
        className: `${STATUS_BADGE_BASE} bg-orange-100 text-orange-800`,
        Icon: CalendarClock,
      };
    }

    if (offer.status === "Approved") {
      return {
        label: "Flash Sale Scheduled",
        className: `${STATUS_BADGE_BASE} bg-orange-100 text-orange-800`,
        Icon: CalendarClock,
      };
    }
    if (offer.status === "Rejected") {
      return getPlainStatusBadge("Rejected");
    }
    return getPlainStatusBadge("Pending");
  }

  if (isExpiredCheck(offer.expiryDate)) {
    return {
      label: "Expired",
      className: `${STATUS_BADGE_BASE} bg-red-100 text-red-800`,
      Icon: TimerOff,
    };
  }

  return getPlainStatusBadge(offer.status);
};

const ActionDropdown = ({
  id,
  activeDropdown,
  setActiveDropdown,
  handleViewModal,
  deal,
  handleEditNavigation,
  handleBlastSMSCampaign,
  handleDeleteClick,
  isExpired,
  handleOpenPromoteModal,
  handleOpenDemoteModal,
  // Permission props
  canEditOffers,
  canDeleteOffers,
  canBlastDeals,
}) => {
  const [dropdownPosition, setDropdownPosition] = useState("top");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown === id) {
      document.addEventListener("mousedown", handleClickOutside);
      const button = document.getElementById(`action-button-${id}`);
      if (button) {
        const rect = button.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 280;
        const newPosition = spaceBelow < dropdownHeight ? "top" : "bottom";
        setDropdownPosition(newPosition);

        setDropdownStyle({
          position: "fixed",
          right: window.innerWidth - rect.right,
          ...(newPosition === "top"
            ? { bottom: window.innerHeight - rect.top + 8 }
            : { top: rect.bottom + 8 }),
        });
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown, id, setActiveDropdown]);

  const actions = [
    {
      icon: <Eye className="w-4 h-4" />,
      label: "View Details",
      color: "text-blue-600",
      onClick: () => handleViewModal(id, deal),
      available: true, // View is always available
    },
  ];

  // Add Promote/Demote actions (only for non-expired, non-pending deals)
  if (!isExpired(deal.expiryDate) && deal.status !== "Pending") {
    if (deal.promote_deal !== 1) {
      actions.push({
        icon: <Megaphone className="w-4 h-4" />,
        label: "Promote Deal",
        color: "text-pink-600",
        onClick: () => handleOpenPromoteModal(deal.deal_slug),
        available: canBlastDeals,
        restricted: !canBlastDeals,
        tooltip: !canBlastDeals ? "Permission required: Blast Deal" : "",
      });
    } else {
      actions.push({
        icon: <FireExtinguisher className="w-4 h-4" />,
        label: "Demote Deal",
        color: "text-pink-600",
        onClick: () => handleOpenDemoteModal(deal.deal_slug),
        available: canBlastDeals,
        restricted: !canBlastDeals,
        tooltip: !canBlastDeals ? "Permission required: Blast Deal" : "",
      });
    }

    // Blast Deal action
    actions.push({
      icon: <Megaphone className="w-4 h-4" />,
      label: "Blast Deal",
      color: "text-green-600",
      onClick: () => handleBlastSMSCampaign(deal),
      available: canBlastDeals,
      restricted: !canBlastDeals,
      tooltip: !canBlastDeals ? "Permission required: Blast Deal" : "",
    });
  }

  // Edit action (always available)
  actions.push({
    icon: <Edit2 className="w-4 h-4" />,
    label: "Edit Offer",
    color: "text-green-600",
    onClick: () => handleEditNavigation(deal),
    available: canEditOffers,
    restricted: !canEditOffers,
    tooltip: !canEditOffers ? "Permission required: Edit Offer" : "",
  });

  // Delete action
  actions.push({
    icon: <Trash2 className="w-4 h-4" />,
    label: "Delete Offer",
    color: "text-red-600",
    onClick: () => handleDeleteClick(deal),
    available: canDeleteOffers,
    restricted: !canDeleteOffers,
    tooltip: !canDeleteOffers ? "Permission required: Delete Offer" : "",
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        id={`action-button-${id}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={(e) => {
          e.stopPropagation();
          setActiveDropdown(activeDropdown === id ? null : id);
        }}
        className="p-2 rounded-lg transition-colors duration-200 hover:bg-gray-100"
      >
        <MoreVertical className="w-5 h-5 text-gray-600" />
      </motion.button>

      <AnimatePresence>
        {activeDropdown === id && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed w-56 rounded-xl bg-white shadow-xl border border-gray-100 py-2 z-[99999]"
            style={dropdownStyle}
          >
            {actions.map((action) => (
              <motion.button
                key={action.label}
                whileHover={action.available ? { x: 4 } : {}}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveDropdown(null);
                  if (action.available && action.onClick) {
                    action.onClick();
                  } else if (action.restricted) {
                    toast.error(
                      action.tooltip ||
                        "You don't have permission for this action"
                    );
                  }
                }}
                disabled={action.restricted}
                title={action.tooltip}
                className={`w-full px-4 py-2 flex items-center space-x-3 transition-all duration-200
                  ${
                    action.available
                      ? `hover:bg-gray-50 ${action.color}`
                      : "opacity-50 cursor-not-allowed text-gray-400"
                  }`}
              >
                <div className="flex items-center space-x-2">
                  {action.icon}
                  {action.restricted && <Lock className="w-3 h-3" />}
                </div>
                <span className="text-sm font-medium">{action.label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="overflow-y-auto fixed inset-0 z-50 p-4 bg-black/50"
    >
      <div className="flex justify-center items-center min-h-screen">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="overflow-hidden relative w-full max-w-sm bg-white rounded-3xl sm:max-w-md"
        >
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center p-4 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto p-4">{children}</div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const labelList = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(Boolean);

const offerCategoryNames = (offer) => {
  const fromObjects = labelList(offer?.categories);
  if (fromObjects.length) return fromObjects;
  return labelList(offer?.category);
};

const offerSubcategoryNames = (offer) => {
  const fromObjects = labelList(offer?.subcategories);
  if (fromObjects.length) return fromObjects;
  return offer?.product_subcategory ? [offer.product_subcategory] : [];
};

const Offers = ({ setSelectedData }) => {
  const user = useSelector((state) => state?.user?.user) || null;
  const subscription_details = user?.subscription;

  // Navigation hook
  const navigate = useNavigate();

  // Permission checks
  const { canPerform, isAuthenticated, isMainVendor } = usePermissionCheck();

  // Check specific permissions
  const canViewDeals =
    canPerform(
      PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      PERMISSIONS.ACTIONS.VIEW_DEALS
    ) || isMainVendor;
  const canCreateOffers =
    canPerform(
      PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      PERMISSIONS.ACTIONS.CREATE_OFFER
    ) || isMainVendor;
  const canEditOffers =
    canPerform(
      PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      PERMISSIONS.ACTIONS.EDIT_OFFER
    ) || isMainVendor;
  const canDeleteOffers =
    canPerform(
      PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      PERMISSIONS.ACTIONS.DELETE_OFFER
    ) || isMainVendor;
  const canBlastDeals =
    canPerform(
      PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      PERMISSIONS.ACTIONS.BLAST_DEAL
    ) || isMainVendor;

  // ALL STATE HOOKS MUST COME BEFORE ANY CONDITIONAL RETURNS
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [deal, setDeal] = useState(null);
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [promotedDeals, setPromotedDeals] = useState(0);
  const [pendingReviewDeals, setPendingReviewDeals] = useState(0);
  const [expiredDeals, setExpiredDeals] = useState(0);
  const [totalDeals, setTotalDeals] = useState(0);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    deal: null,
  });
  const [promoteModal, setPromoteModal] = useState({
    isOpen: false,
    deal_slug: null,
  });
  const [demoteModal, setDemoteModal] = useState({
    isOpen: false,
    deal_slug: null,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [totalDealsPosted, setTotalDealsPosted] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [modalContent, setModalContent] = useState({
    type: null,
    data: null,
    isOpen: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // ALL EFFECT HOOKS MUST COME BEFORE ANY CONDITIONAL RETURNS
  useEffect(() => {
    const createdMessage = sessionStorage.getItem(DEAL_CREATED_TOAST_KEY);
    if (createdMessage) {
      sessionStorage.removeItem(DEAL_CREATED_TOAST_KEY);
      toast.success(createdMessage);
    }
  }, []);

  useEffect(() => {
    const fetchVendorPlansDetails = async () => {
      try {
        setIsLoading(true);
        const response = await VendorRequests.getVendorPlansDetails();

        if (
          response?.data?.status_code === "000" &&
          !response?.data?.in_error
        ) {
          setTotalDealsPosted(response?.data?.data?.total_deals);
          setIsLoading(false);
        } else {
          toast.error(
            "Sorry, an error occurred while fetching your plans details"
          );
        }
      } catch (error) {
        toast.error(
          "Sorry, an error occurred while fetching your plans details"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchVendorPlansDetails();
  }, []);

  useEffect(() => {
    const fetchDeals = async () => {
      setIsLoading(true);
      try {
        const response = await DealsRequest.getAllDeals();
        if (response && response.data) {
          const currentDate = new Date();
          const dealsData = response.data;

          setDeals(dealsData);
          setTotalDeals(dealsData.length);

          // Count approved deals
          const approvedCount = dealsData.filter(
            (deal) => deal.status === "Approved"
          ).length;
          setPromotedDeals(approvedCount);

          // Count pending deals
          const pendingCount = dealsData.filter(
            (deal) => deal.status === "Pending"
          ).length;
          setPendingReviewDeals(pendingCount);

          //* Count expired deals (either status is expired or expiry date has passed)
          const expiredCount = dealsData.filter((deal) => {
            const expiryDate = deal.expiryDate
              ? new Date(deal.expiryDate)
              : null;
            return (
              deal.status === "expired" ||
              (expiryDate && expiryDate < currentDate)
            );
          }).length;

          setExpiredDeals(expiredCount);
        } else {
          setDeals([]);
          setTotalDeals(0);
          setPromotedDeals(0);
          setPendingReviewDeals(0);
          setExpiredDeals(0);
        }
      } catch (error) {
        console.error("Error fetching deals:", error);
        toast.error("Failed to fetch deals");
        setDeals([]);
        setTotalDeals(0);
        setPromotedDeals(0);
        setPendingReviewDeals(0);
        setExpiredDeals(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDeals();
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // NOW CONDITIONAL RETURNS CAN HAPPEN AFTER ALL HOOKS
  // Access denied modal for users without view permissions
  if (!isAuthenticated || !canViewDeals) {
    return (
      <PermissionDeniedModal message="You do not have permission to view offers." />
    );
  }

  // HELPER FUNCTIONS COME AFTER CONDITIONAL RETURNS
  //* Helper function to check if a deal is expired
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const currentDate = new Date();
    const expiry = new Date(expiryDate);
    return expiry < currentDate;
  };

  const handleEditNavigation = (deal) => {
    navigate(`/offers/edit/${deal.slug}`);
  };

  const handleBlastSMSCampaign = (deal) => {
    // Subscription guard commented out
    // if (isSubscriptionExpired()) {
    //   toast.error(
    //     "Your subscription has expired. Please renew to blast deals.",
    //     {
    //       icon: "⚠️",
    //     }
    //   );
    //   return;
    // }
    navigate(`/deal/blast/${deal.slug}`);
  };

  // const handleCampaignNavigation = (deal) => {
  //   navigate(`/campaigns/create/${deal.slug}`);
  // };

  const handleViewModal = (id, deal) => {
    setViewModal(true);
    setDeal(deal);
  };

  const handleCloseViewModal = () => {
    setViewModal(false);
  };

  const handleSelectAll = () => {
    if (selectedItems.length === deals.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(deals.map((deal) => deal.id));
    }
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getImageSource = (imageData) => {
    if (!imageData) {
      return "https://placehold.co/48x48/FF6C6C/FFF?text=DB";
    }

    if (
      typeof imageData === "string" &&
      (imageData.startsWith("http://") || imageData.startsWith("https://"))
    ) {
      return imageData;
    }

    if (imageData.startsWith("data:image")) {
      return imageData;
    }

    return `data:image/png;base64,${imageData}`;
  };

  const PLACEHOLDER_IMAGE = "https://placehold.co/48x48/FF6C6C/FFF?text=DB";

  const handleImageError = (event) => {
    event.currentTarget.src = PLACEHOLDER_IMAGE;
  };

  const getSortedData = () => {
    let filteredDeals = deals;

    // Apply search filter if searchTerm exists
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredDeals = deals.filter(
        (deal) =>
          deal.title?.toLowerCase().includes(searchLower) ||
          getDealProductName(deal).toLowerCase().includes(searchLower) ||
          deal.category?.some((cat) =>
            String(typeof cat === "string" ? cat : cat?.name || "")
              .toLowerCase()
              .includes(searchLower)
          ) ||
          deal.categories?.some((cat) =>
            String(cat?.name || "").toLowerCase().includes(searchLower)
          ) ||
          deal.subcategories?.some((sub) =>
            String(sub?.name || "").toLowerCase().includes(searchLower)
          ) ||
          String(deal.product_subcategory || "").toLowerCase().includes(searchLower) ||
          deal.status?.toLowerCase().includes(searchLower) ||
          (deal.price?.toString() || "").includes(searchLower) ||
          (deal.discountedPrice?.toString() || "").includes(searchLower) ||
          (deal.discountPercentage?.toString() || "").includes(searchLower) ||
          deal.region_location?.some((loc) =>
            (typeof loc === "object"
              ? `${loc.region} ${loc.location}`.toLowerCase()
              : loc.toLowerCase()
            ).includes(searchLower)
          )
      );
    }

    // Apply sorting
    if (!sortConfig.key) return filteredDeals;

    return [...filteredDeals].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const handleCloseModal = () => {
    setModalContent({ type: null, data: null, isOpen: false });
  };

  const getPaginatedData = () => {
    const sortedData = getSortedData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNewOffer = () => {
    // Subscription guard commented out
    // if (isSubscriptionExpired()) {
    //   toast.error(
    //     "Your subscription has expired. Please renew to create new offers.",
    //     {
    //       icon: "⚠️",
    //     }
    //   );
    //   navigate("/pricing");
    //   return;
    // }
    navigate("/offers/new");
  };

  const handleDeleteClick = (deal) => {
    setDeleteModal({ isOpen: true, deal });
    setActiveDropdown(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      const deleteResponse = await DealsRequest.deleteDeal(
        deleteModal.deal.slug
      );
      console.log(deleteResponse.data.status_code);
      if (deleteResponse.data.status_code === "997") {
        toast.error(
          "This deal has already been redeemed by at least one subscriber"
        );
        return;
      }

      toast.success(
        `"${getVendorDealLabel(deleteModal.deal)}" has been successfully deleted`,
        {
          icon: "🗑️",
        }
      );

      const response = await DealsRequest.getAllDeals();
      if (response && response.data) {
        setDeals(response.data);
        setTotalDeals(response.data.length);
        setPromotedDeals(
          response.data.filter((deal) => deal.status === "Approved").length
        );
        setPendingReviewDeals(
          response.data.filter((deal) => deal.status === "Pending").length
        );
        setExpiredDeals(
          response.data.filter((deal) => deal.status === "expired").length
        );
      }

      setDeleteModal({ isOpen: false, deal: null });
    } catch (error) {
      toast.error(error.message || "Failed to delete deal", {
        icon: "❌",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add a helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace("GHS", "GH₵");
  };

  const handleBulkDelete = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${
          selectedItems.length
        } selected offer${selectedItems.length > 1 ? "s" : ""}?`
      )
    ) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      // Delete each selected deal
      for (const id of selectedItems) {
        const deal = deals.find((d) => d.id === id);
        if (deal) {
          await DealsRequest.deleteDeal(deal.slug);
        }
      }

      toast.success(
        `Successfully deleted ${selectedItems.length} offer${
          selectedItems.length > 1 ? "s" : ""
        }`,
        {
          icon: "🗑️",
        }
      );

      // Refresh deals list
      const response = await DealsRequest.getAllDeals();
      if (response && response.data) {
        setDeals(response.data);
        setTotalDeals(response.data.length);
        setPromotedDeals(
          response.data.filter((deal) => deal.status === "Approved").length
        );
        setPendingReviewDeals(
          response.data.filter((deal) => deal.status === "Pending").length
        );
        setExpiredDeals(
          response.data.filter((deal) => isExpired(deal.expiryDate)).length
        );
      }

      // Clear selection
      setSelectedItems([]);
    } catch (error) {
      toast.error("Failed to delete some offers. Please try again.", {
        icon: "❌",
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Update this function to check if deals length is greater than or equal to totalDealsPosted
  const shouldShowUpgradeNotice = () => {
    // Subscription guard commented out
    // if (isSubscriptionExpired()) return true;

    // const max_offers =
    //   subscription_details?.subscription_plan?.features?.max_offers ||
    //   subscription_details?.subscription_plan?.features?.max_deals;
    // return (
    //   parseInt(deals.length) >= parseInt(max_offers) && parseInt(max_offers) > 0
    // );
    return false; // Always return false to disable upgrade notice
  };

  const handleOpenPromoteModal = (dealSlug) => {
    setPromoteModal({
      isOpen: true,
      deal_slug: dealSlug,
    });
  };
  const handleOpenDemoteModal = (dealSlug) => {
    setDemoteModal({
      isOpen: true,
      deal_slug: dealSlug,
    });
  };

  const handlePromoteModalClose = () => {
    setPromoteModal({ isOpen: false, deal_slug: null });
  };

  // Add a function to refresh the deals data
  const refreshDealsData = async () => {
    try {
      setIsLoading(true);
      const response = await DealsRequest.getAllDeals();
      if (response && response.data) {
        const dealsData = response.data;
        setDeals(dealsData);
        setTotalDeals(dealsData.length);

        // Update other counts
        const approvedCount = dealsData.filter(
          (deal) => deal.status === "Approved"
        ).length;
        setPromotedDeals(approvedCount);

        const pendingCount = dealsData.filter(
          (deal) => deal.status === "Pending"
        ).length;
        setPendingReviewDeals(pendingCount);

        const expiredCount = dealsData.filter((deal) => {
          const expiryDate = deal.expiryDate ? new Date(deal.expiryDate) : null;
          return (
            deal.status === "expired" || (expiryDate && expiryDate < new Date())
          );
        }).length;
        setExpiredDeals(expiredCount);
      }
    } catch (error) {
      console.error("Error refreshing deals:", error);
      toast.error("Failed to refresh deals data");
    } finally {
      setIsLoading(false);
    }
  };

  // Add function to check if subscription is expired
  const isSubscriptionExpired = () => {
    if (!subscription_details?.end_date) return true;
    const endDate = new Date(subscription_details.end_date);
    const currentDate = new Date();
    return endDate < currentDate;
  };

  const getLocationDisplay = (offer) => {
    if (!offer) {
      return {
        locations: [],
        isPartnerLocation: false,
        locationType: 'none'
      };
    }

    // Handle new redemption_partners format (highest priority)
    if (
      offer.redemption_partners &&
      Array.isArray(offer.redemption_partners) &&
      offer.redemption_partners.length > 0
    ) {
      const formattedLocations = offer.redemption_partners.flatMap(
        (partner) => {
          if (
            partner &&
            partner.branches &&
            Array.isArray(partner.branches) &&
            partner.branches.length > 0
          ) {
            return partner.branches
              .filter(branch => branch && (branch.location || branch.region || branch.name || branch.branch_name))
              .map((branch) => ({
                region: branch.region || branch.region_name || "N/A",
                location: branch.location || branch.location_name || "N/A",
                name: branch.name || branch.branch_name || branch.location || "N/A",
                partner_name: partner.partner_name || partner.name || "N/A",
              }));
          }
          return [];
        }
      );

      if (formattedLocations.length > 0) {
        return {
          locations: formattedLocations,
          isPartnerLocation: true,
          locationType: "redemption",
        };
      }
    }

    // Check for target locations in order of priority
    const targetLocations =
      offer.target_branches ||
      offer.target_partners ||
      offer.region_location ||
      [];

    if (targetLocations && targetLocations.length > 0) {
      // Handle target_branches (array of branch objects)
      if (
        offer.target_branches &&
        Array.isArray(offer.target_branches) &&
        offer.target_branches.length > 0
      ) {
        const formattedLocations = offer.target_branches.map((branch) => ({
          region: branch.region || "N/A",
          location: branch.location || "N/A",
          name: branch.name || branch.branch_name || "N/A",
          partner_name: "Target Branch",
        }));

        return {
          locations: formattedLocations,
          isPartnerLocation: false,
          locationType: "target",
        };
      }

      // Handle target_partners (array of partner objects with branches)
      if (
        offer.target_partners &&
        Array.isArray(offer.target_partners) &&
        offer.target_partners.length > 0
      ) {
        const formattedLocations = offer.target_partners.flatMap((partner) => {
          if (
            partner.branches &&
            Array.isArray(partner.branches) &&
            partner.branches.length > 0
          ) {
            return partner.branches.map((branch) => ({
              region: branch.region || "N/A",
              location: branch.location || "N/A",
              name: branch.name || "N/A",
              partner_name: partner.partner_name || "N/A",
            }));
          }
          return [];
        });

        if (formattedLocations.length > 0) {
          return {
            locations: formattedLocations,
            isPartnerLocation: true,
            locationType: "target",
          };
        }
      }

      // Handle region_location (original format)
      if (
        offer.region_location &&
        Array.isArray(offer.region_location) &&
        offer.region_location.length > 0
      ) {
        // Ensure all items are properly formatted
        const formattedLocations = offer.region_location.map((loc) => {
          if (typeof loc === "object" && loc !== null) {
            return {
              region: loc.region || "N/A",
              location: loc.location || "N/A",
              name: loc.name || "N/A",
              partner_name: loc.partner_name || "N/A",
            };
          }
          return {
            region: "N/A",
            location: loc || "N/A",
            name: "N/A",
            partner_name: "N/A",
          };
        });

        return {
          locations: formattedLocations,
          isPartnerLocation: false,
          locationType: "region",
        };
      }
    }

    // Fall back to partner_location if region_location is empty
    if (offer.partner_location) {
      try {
        // Parse the stringified partner_location array
        const partnerLocations =
          typeof offer.partner_location === "string"
            ? JSON.parse(offer.partner_location)
            : offer.partner_location;

        if (
          partnerLocations &&
          Array.isArray(partnerLocations) &&
          partnerLocations.length > 0
        ) {
          const formattedLocations = partnerLocations.flatMap((partner) => {
            if (
              partner.branches &&
              Array.isArray(partner.branches) &&
              partner.branches.length > 0
            ) {
              return partner.branches.map((branch) => ({
                region: branch.region || "N/A",
                location: branch.location || "N/A",
                name: branch.name || "N/A",
                partner_name: partner.partner_name || "N/A",
              }));
            }
            return [];
          });

          if (formattedLocations.length > 0) {
            return {
              locations: formattedLocations,
              isPartnerLocation: true,
              locationType: "legacy",
            };
          }
        }
      } catch (error) {
        console.error("Error parsing partner_location:", error);
      }
    }

    // Return empty array if all are empty/invalid
    return {
      locations: [],
      isPartnerLocation: false,
      locationType: "none",
    };
  };

  // Todo =>Add a helper function to render delivery status badge
  const renderDeliveryStatusBadge = (smsDeliveryStatus) => {
    if (!smsDeliveryStatus || smsDeliveryStatus === null) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-red-600 bg-red-100 rounded-full">
          <Send className="mr-1 w-3 h-3" />
          Deal Not Blasted
        </span>
      );
    }

    switch (smsDeliveryStatus.toLowerCase()) {
      case "completed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-green-800 bg-green-100 rounded-full">
            <CheckCircle className="mr-1 w-3 h-3" />
            Completed
          </span>
        );
      case "partial":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full">
            <AlertCircle className="mr-1 w-3 h-3" />
            Partial
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-red-600 bg-red-100 rounded-full">
            <Send className="mr-1 w-3 h-3" />
            Deal Not Blasted
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {viewModal && (
        <ViewOffer
          viewModal={viewModal}
          onCloseViewModal={handleCloseViewModal}
          deal={deal}
          handleEditNavigation={handleEditNavigation}
        />
      )}

      {promoteModal.isOpen && (
        <PromoteDealModal
          promoteModal={promoteModal}
          setPromoteModal={setPromoteModal}
          dealSlug={promoteModal.deal_slug}
          onSuccess={refreshDealsData}
        />
      )}
      {demoteModal.isOpen && (
        <DemoteDealModal
          setDemoteModal={setDemoteModal}
          dealSlug={demoteModal.deal_slug}
          onSuccess={refreshDealsData}
        />
      )}
      <Navbar page="Offers" />
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-4 py-6 bg-white shadow-sm sm:px-6 lg:px-8"
      >
        <LimitedAccessMessage />
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col justify-between items-start space-y-4 sm:flex-row sm:items-center sm:space-y-0">
            <div className="space-y-1">
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-2 items-center text-2xl font-bold text-gray-900"
              >
                <Package className="w-6 h-6 text-[#FF6C6C]" />
                Your Offers
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-sm text-gray-500"
              >
                Manage and track all your business offers in one place
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center space-x-4"
            >
              <div className="hidden relative sm:block">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search offers..."
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl 
                    focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 
                    focus:border-[#FF6C6C] w-64 text-sm transition-all duration-200"
                />
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 p-1 rounded-full transition-colors duration-200 transform -translate-y-1/2 hover:bg-gray-100"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>

              <motion.button
                onClick={
                  !canCreateOffers
                    ? () =>
                        toast.error(
                          "You don't have permission to create offers"
                        )
                    : handleNewOffer
                    // Subscription guard commented out
                    // : shouldShowUpgradeNotice()
                    // ? () => navigate("/pricing")
                    // : handleNewOffer
                }
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={!canCreateOffers}
                className={`inline-flex items-center px-4 py-2 rounded-xl transition-all duration-200
                  ${
                    !canCreateOffers
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed opacity-60"
                      : "bg-gradient-to-r from-[#FF6C6C] to-pink-600 text-white shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40"
                      // Subscription guard commented out
                      // : shouldShowUpgradeNotice()
                      // ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      // : "bg-gradient-to-r from-[#FF6C6C] to-pink-600 text-white shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40"
                  }`}
                title={
                  !canCreateOffers ? "Permission required: Create Offer" : ""
                }
              >
                {!canCreateOffers ? (
                  <>
                    <Lock className="mr-1.5 w-5 h-5" />
                    Create Offer
                  </>
                ) : (
                  <>
                    <Plus className="mr-1.5 w-5 h-5" />
                    Create Offer
                  </>
                )}
                {/* Subscription guard commented out */}
                {/* ) : shouldShowUpgradeNotice() ? (
                  <>
                    <ArrowUpDown className="mr-1.5 w-5 h-5" />
                    Upgrade to Create More
                  </>
                ) : (
                  <>
                    <Plus className="mr-1.5 w-5 h-5" />
                    Create Offer
                  </>
                )} */}
              </motion.button>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 gap-4 mt-6 sm:grid-cols-4"
          >
            {[
              { label: "Total Offers", value: totalDeals },
              { label: "Approved Offers", value: promotedDeals },
              { label: "Pending Review", value: pendingReviewDeals },
              { label: "Expired", value: expiredDeals },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className="p-4 rounded-xl border border-gray-100 backdrop-blur-sm bg-white/50"
              >
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {stat.value}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Show search results count when searching */}
          {searchTerm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 text-sm text-gray-500"
            >
              Found {getSortedData().length} results for "{searchTerm}"
            </motion.div>
          )}

          {/* Important Notice */}
          {/* TODO: Uncomment if needed
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-4 mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
          >
            <div className="flex gap-3 items-start">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="mb-1 text-sm font-semibold text-blue-800">
                  Important Notice: Deal Editing Policy
                </h3>
                <p className="text-sm leading-relaxed text-blue-700">
                  Once your deal is approved (within 24 hours), you won't be
                  able to edit its details. If you need to make changes to an
                  approved deal, please{" "}
                  <span className="font-medium">contact our support team</span>{" "}
                  or{" "}
                  <span className="font-medium">
                    delete and recreate the deal
                  </span>{" "}
                  for approval again.
                </p>
              </div>
            </div>
          </motion.div>
          */}
        </div>
      </motion.div>

      {/* After the stats grid and before the table */}
      {/* Subscription guard commented out */}
      {/* <AnimatePresence>
        {shouldShowUpgradeNotice() && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="px-4 mx-auto my-6 max-w-7xl sm:px-6 lg:px-8"
          >
            <div className="p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl border border-yellow-200">
              <div className="flex flex-wrap gap-4 justify-between items-center">
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-yellow-200 rounded-lg">
                    <Package className="w-5 h-5 text-yellow-700" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">
                      {isSubscriptionExpired()
                        ? "Subscription Expired"
                        : "Package Limit Reached"}
                    </h3>
                    <p className="text-sm text-yellow-600">
                      {isSubscriptionExpired()
                        ? "Your subscription has expired. Please renew to continue using all features."
                        : "You've reached the maximum number of deals for your current package."}
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/pricing")}
                  className="inline-flex items-center px-4 py-2 font-medium text-white bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl shadow-lg transition-all duration-200 shadow-yellow-500/30 hover:shadow-xl hover:shadow-yellow-500/40"
                >
                  <ArrowUpDown className="mr-2 w-4 h-4" />
                  {isSubscriptionExpired()
                    ? "Renew Subscription"
                    : "Upgrade Package"}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}

      {/* Bulk Delete Bar */}
      <AnimatePresence>
        {selectedItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-0 z-50 p-4 bg-white border-b border-gray-200 shadow-lg"
          >
            <div className="flex justify-between items-center mx-auto max-w-7xl">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-600">
                  {selectedItems.length} item
                  {selectedItems.length > 1 ? "s" : ""} selected
                </span>
                <button
                  onClick={() => setSelectedItems([])}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear selection
                </button>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={
                  canDeleteOffers
                    ? handleBulkDelete
                    : () =>
                        toast.error(
                          "You don't have permission to delete offers"
                        )
                }
                disabled={isBulkDeleting || !canDeleteOffers}
                className={`inline-flex items-center px-4 py-2 font-medium rounded-xl shadow-lg transition-all duration-200 shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 disabled:opacity-50 disabled:cursor-not-allowed
                  ${
                    canDeleteOffers
                      ? "text-white bg-red-500 hover:bg-red-600"
                      : "text-gray-500 bg-gray-200 cursor-not-allowed"
                  }`}
                title={
                  !canDeleteOffers ? "Permission required: Delete Offer" : ""
                }
              >
                {!canDeleteOffers ? (
                  <>
                    <Lock className="mr-2 w-4 h-4" />
                    Delete Selected
                  </>
                ) : isBulkDeleting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    <span>Deleting...</span>
                  </div>
                ) : (
                  <>
                    <Trash2 className="mr-2 w-4 h-4" />
                    Delete Selected
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Adjust main content padding when bulk delete bar is shown */}
      <div
        className={`px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8 ${
          selectedItems.length > 0 ? "mt-16" : ""
        }`}
      >
        <div className="overflow-hidden bg-white rounded-xl shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left">
                    <div className="flex items-center">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleSelectAll}
                        className="text-gray-500 hover:text-[#FF6C6C]"
                      >
                        {selectedItems.length === deals.length ? (
                          <CheckSquare className="w-5 h-5" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </motion.button>
                    </div>
                  </th>
                  {[
                    { key: "image", label: "Image" },
                    { key: "title", label: "Title / Product" },
                    { key: "categories", label: "Categories" },
                    { key: "offer_type", label: "Offer Type" },
                    { key: "price", label: "Original Price" },
                    { key: "discountedPrice", label: "Discount Price" },
                    { key: "discountPercentage", label: "Discount %" },
                    { key: "createdAt", label: "Created" },
                    { key: "expiryDate", label: "Expires" },
                    { key: "status", label: "Status" },
                    { key: "promotion_status", label: "Promotion" },
                    { key: "delivery_status", label: "Delivery Status" },
                    { key: "actions", label: "Actions" },
                  ].map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className="px-6 py-3 text-left whitespace-nowrap"
                    >
                      <div className="flex gap-1 items-center group">
                        <span className="text-xs font-medium tracking-wider text-gray-500 uppercase truncate">
                          {column.label}
                        </span>
                        <button
                          onClick={() => handleSort(column.key)}
                          className="flex-shrink-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                        >
                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 transform transition-transform duration-200
                            ${
                              sortConfig.key === column.key &&
                              sortConfig.direction === "desc"
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getPaginatedData().map((offer) => (
                  <tr key={offer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleSelectItem(offer.id)}
                        className="text-gray-500 hover:text-[#FF6C6C]"
                      >
                        {selectedItems.includes(offer.id) ? (
                          <CheckSquare className="w-5 h-5" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </motion.button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex shrink-0 justify-center items-center overflow-hidden w-12 h-12 rounded-lg border border-gray-100 bg-gray-50">
                        <img
                          src={getImageSource(offer.image)}
                          alt={getVendorDealLabel(offer) || "Deal"}
                          className="object-contain w-full h-full p-0.5"
                          onError={handleImageError}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VendorDealTitle deal={offer} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const categories = offerCategoryNames(offer);
                        const subcategories = offerSubcategoryNames(offer);
                        const extra = Math.max(categories.length - 1, 0) + Math.max(subcategories.length - 1, 0);
                        return (
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex min-w-0 flex-wrap items-center gap-1">
                              {categories[0] && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FF6C6C]/10 text-[#FF6C6C]">
                                  {categories[0]}
                                </span>
                              )}
                              {subcategories[0] && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  {subcategories[0]}
                                </span>
                              )}
                            </div>
                            {extra > 0 && (
                              <button
                                onClick={() =>
                                  setModalContent({
                                    type: "categories",
                                    data: { categories, subcategories },
                                    isOpen: true,
                                  })
                                }
                                className="flex-shrink-0 px-2 py-0.5 text-xs text-gray-500 bg-gray-100 rounded-full transition-colors duration-200 hover:bg-gray-200"
                              >
                                +{extra}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(() => {
                          // Determine offer type based on data
                          if (
                            offer.bundle &&
                            offer.bundle.length > 0 &&
                            offer.bundle.some(
                              (bundle) =>
                                bundle.original_sum && bundle.bundle_price
                            )
                          ) {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-full">
                                Bundle
                              </span>
                            );
                          } else if (
                            offer.bogo &&
                            offer.bogo.length > 0 &&
                            offer.bogo.some(
                              (bogo) =>
                                bogo.bogo_item_price ||
                                (bogo.bogo_quantity && bogo.bogo_free_quantity)
                            )
                          ) {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                BOGO
                              </span>
                            );
                          } else if (offer.price && offer.price !== 0) {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                Discount
                              </span>
                            );
                          } else {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                                {offer.offer_type || "Standard"}
                              </span>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    {(() => {
                      const pricing = getOfferPricingDetails(offer);
                      const hasDiscountedPrice =
                        pricing.discounted != null &&
                        pricing.discounted !== 0 &&
                        pricing.discounted !== "0";

                      return (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {pricing.original != null &&
                            pricing.original !== 0 &&
                            pricing.original !== "0" ? (
                              <span
                                className={`text-sm font-medium ${
                                  hasDiscountedPrice
                                    ? "text-gray-400 line-through decoration-gray-300"
                                    : "text-gray-700"
                                }`}
                              >
                                {formatCurrency(pricing.original)}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {hasDiscountedPrice ? (
                              <span className="text-sm font-semibold text-emerald-700">
                                {formatCurrency(pricing.discounted)}
                              </span>
                            ) : pricing.variant === "bogo" && pricing.bogoLabel ? (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-full border border-blue-100">
                                {pricing.bogoLabel}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {pricing.discountPercent > 0 ? (
                              <DiscountPercentBadge
                                percent={pricing.discountPercent}
                              />
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                        </>
                      );
                    })()}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {moment(offer.createdAt).format("YYYY-MMM-DD")}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(() => {
                          // Check if it's a flash sale deal
                          if (offer?.validity_type === "flash_sale") {
                            return (
                              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-700 bg-gradient-to-r from-red-50 to-orange-50 rounded-md border border-red-200">
                                <svg
                                  className="mr-1.5 w-3 h-3 text-red-500"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 10V3L4 14h7v7l9-11h-7z"
                                  />
                                </svg>
                                Flash Sale Deal
                              </span>
                            );
                          }

                          // Regular expiry date logic
                          if (offer.expiryDate) {
                            return (
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-md transition-colors duration-200
                                  ${
                                    isExpired(offer.expiryDate)
                                      ? "bg-red-100 text-red-800"
                                      : "text-gray-800 bg-gray-100 hover:bg-gray-200"
                                  }`}
                              >
                                {moment(offer.expiryDate).format("YYYY-MMM-DD")}
                              </span>
                            );
                          }

                          return (
                            <span className="text-gray-400">
                              No expiry date
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const { label, className, Icon } = getOfferStatusBadge(
                          offer,
                          isExpired
                        );
                        return (
                          <span className={className}>
                            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                            {label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${
                          offer.promote_deal === 1 || 
                          offer.promote_deal === true
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {offer.promote_deal === 1 || offer.promote_deal === true ? (
                          <>
                            <Megaphone className="mr-1 w-3 h-3" />
                            Promoted
                          </>
                        ) : (
                          "Not Promoted"
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderDeliveryStatusBadge(offer?.sms_delivery_status)}
                    </td>
                    <td className="relative px-6 py-4 text-sm text-right whitespace-nowrap">
                      <PermissionAwareActionDropdown
                        id={offer.id}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                        handleViewModal={handleViewModal}
                        deal={offer}
                        handleEditNavigation={handleEditNavigation}
                        handleBlastSMSCampaign={handleBlastSMSCampaign}
                        handleDeleteClick={handleDeleteClick}
                        isExpired={isExpired}
                        handleOpenPromoteModal={handleOpenPromoteModal}
                        handleOpenDemoteModal={handleOpenDemoteModal}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
        <div className="flex flex-1 justify-between sm:hidden">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium
                        shadow-sm transition-all duration-200 gap-2
                        ${
                          currentPage === 1
                            ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border border-gray-200"
                        }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= Math.ceil(deals.length / itemsPerPage)}
            className={`relative inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium
                        shadow-sm transition-all duration-200 gap-2
                        ${
                          currentPage >= Math.ceil(deals.length / itemsPerPage)
                            ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border border-gray-200"
                        }`}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </motion.button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {Math.min(
                  (currentPage - 1) * itemsPerPage + 1,
                  getSortedData().length
                )}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, getSortedData().length)}
              </span>{" "}
              of <span className="font-medium">{getSortedData().length}</span>{" "}
              results
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium
                          shadow-sm transition-all duration-200 gap-2
                          ${
                            currentPage === 1
                              ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                              : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border border-gray-200"
                          }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </motion.button>

            <div className="flex gap-1 items-center">
              {[...Array(Math.ceil(deals.length / itemsPerPage))].map(
                (_, index) => (
                  <motion.button
                    key={index + 1}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium
                              transition-all duration-200
                              ${
                                currentPage === index + 1
                                  ? "bg-gradient-to-r from-[#FF6C6C] to-pink-600 text-white shadow-lg shadow-pink-500/30"
                                  : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
                              }`}
                  >
                    {index + 1}
                  </motion.button>
                )
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= Math.ceil(deals.length / itemsPerPage)}
              className={`relative inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium
                          shadow-sm transition-all duration-200 gap-2
                          ${
                            currentPage >=
                            Math.ceil(deals.length / itemsPerPage)
                              ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                              : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border border-gray-200"
                          }`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalContent.isOpen && modalContent.type === "categories"}
        onClose={handleCloseModal}
        title="Categories"
      >
        <div className="space-y-5 p-2 max-h-[60vh] overflow-y-auto">
          {[
            { label: "Categories", items: modalContent.data?.categories || (Array.isArray(modalContent.data) ? modalContent.data : []) },
            { label: "Subcategories", items: modalContent.data?.subcategories || [] },
          ].filter((group) => group.items.length > 0).map((group) => (
            <div key={group.label}>
              <p className="mb-2 text-xs font-semibold tracking-wide text-gray-500">{group.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {group.items.map((category, index) => (
                  <motion.div
                    key={`${group.label}-${category}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{
                      delay: index * 0.05,
                      type: "spring",
                      stiffness: 300,
                    }}
                    className="flex items-center sm:flex-col sm:items-center p-4 rounded-2xl
                              bg-[#FFF5F5] space-x-4 sm:space-x-0 sm:space-y-3
                              border border-[#FF6C6C]/10
                              transition-all duration-200"
                  >
                    <motion.div
                      className="p-3 bg-white rounded-xl shadow-sm"
                      whileHover={{ rotate: [0, -10, 10, 0] }}
                    >
                      <Package className="w-6 h-6 text-[#FF6C6C]" />
                    </motion.div>
                    <span className="text-sm font-medium text-[#FF6C6C] break-words sm:text-center">
                      {category}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        isOpen={modalContent.isOpen && modalContent.type === "locations"}
        onClose={handleCloseModal}
        title="All Target Locations"
      >
        <div className="grid grid-cols-1 gap-3">
          {modalContent.data?.map((location, index) => (
            <motion.div
              key={`location-${index}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ x: 10 }}
              transition={{
                delay: index * 0.1,
                type: "spring",
                stiffness: 300,
              }}
              className="flex gap-4 items-center px-5 py-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 shadow-sm transition-all duration-200 group hover:from-gray-100 hover:to-gray-50 hover:border-gray-200 hover:shadow-md"
            >
              <motion.div
                className={`p-2.5 rounded-xl shadow-sm transition-all duration-200 group-hover:shadow-md ${
                  modalContent.locationType === "target"
                    ? "bg-blue-50"
                    : modalContent.isPartnerLocation
                    ? "bg-purple-50"
                    : "bg-white"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <MapPin
                  className={`w-5 h-5 ${
                    modalContent.locationType === "target"
                      ? "text-blue-500"
                      : modalContent.isPartnerLocation
                      ? "text-purple-500"
                      : "text-[#FF6C6C]"
                  }`}
                />
              </motion.div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                  {location}
                </span>
                <span className="text-xs text-gray-500">
                  {modalContent.locationType === "target"
                    ? "Target Location"
                    : modalContent.isPartnerLocation
                    ? "Partner Branch Location"
                    : "Redemption Location"}
                </span>
              </div>
              <motion.div
                className="ml-auto opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
              >
                <ArrowRight
                  className={`w-4 h-4 ${
                    modalContent.locationType === "target"
                      ? "text-blue-500"
                      : modalContent.isPartnerLocation
                      ? "text-purple-500"
                      : "text-[#FF6C6C]"
                  }`}
                />
              </motion.div>
            </motion.div>
          ))}
        </div>
      </Modal>

      {deleteModal.isOpen && (
        <DeleteOfferModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal({ isOpen: false, deal: null })}
          onConfirm={handleDeleteConfirm}
          offerTitle={getVendorDealLabel(deleteModal.deal)}
          isDeleting={isLoading}
        />
      )}
    </div>
  );
};

export default Offers;
